import { useState, useEffect } from 'react';
import { Calendar, Edit2, Trash2, AlertCircle, Clock, CheckCircle, XCircle, MinusCircle } from '../../icons/wp-icons';
import { getMeineAbwesenheiten, deleteAbwesenheit, getStatistik } from '../../api/abwesenheitApi';
import { useAuth } from '../../context/AuthContext';

const TYP_CONFIG = {
  URLAUB: { label: 'Urlaub', color: 'bg-green-100 text-green-700', icon: '🏖️' },
  KRANKHEIT: { label: 'Krankheit', color: 'bg-red-100 text-red-700', icon: '🤒' },
  FORTBILDUNG: { label: 'Fortbildung', color: 'bg-blue-100 text-blue-700', icon: '📚' },
  HOMEOFFICE: { label: 'Homeoffice', color: 'bg-orange-100 text-orange-700', icon: '🏠' },
  SONSTIGES: { label: 'Sonstiges', color: 'bg-gray-100 text-gray-700', icon: '📝' }
};

const STATUS_CONFIG = {
  BEANTRAGT: { label: 'Beantragt', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  GENEHMIGT: { label: 'Genehmigt', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  ABGELEHNT: { label: 'Abgelehnt', color: 'bg-red-100 text-red-700', icon: XCircle },
  STORNIERT: { label: 'Storniert', color: 'bg-gray-100 text-gray-500', icon: MinusCircle }
};

function AbwesenheitMeine({ onEdit, onRefresh }) {
  const { user } = useAuth();
  const [abwesenheiten, setAbwesenheiten] = useState([]);
  const [statistik, setStatistik] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [jahr, setJahr] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, [jahr]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [meineData, statsData] = await Promise.all([
        getMeineAbwesenheiten(jahr),
        getStatistik(jahr)
      ]);
      setAbwesenheiten(meineData);
      // Finde eigene Statistik
      const meineStats = statsData.find(s => s.benutzerId === user?.id);
      setStatistik(meineStats);
    } catch (err) {
      console.error('Fehler:', err);
      setError('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Möchten Sie diese Abwesenheit wirklich löschen/stornieren?')) {
      return;
    }

    try {
      setDeleting(id);
      await deleteAbwesenheit(id);
      await loadData();
      onRefresh?.();
    } catch (err) {
      console.error('Fehler:', err);
      alert(err.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateRange = (start, end) => {
    const startStr = formatDate(start);
    const endStr = formatDate(end);
    if (startStr === endStr) {
      return startStr;
    }
    return `${startStr} - ${endStr}`;
  };

  // Jahre für Dropdown
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Fehler */}
      {error && (
        <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Urlaubstage-Übersicht */}
      {statistik && (
        <div className="bg-surface rounded-card shadow-card p-6">
          <h2 className="text-lg font-semibold mb-4">Meine Urlaubstage {jahr}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{statistik.urlaubstageGesamt}</div>
              <div className="text-sm text-text-muted">Gesamt</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{statistik.urlaubstageVerbraucht}</div>
              <div className="text-sm text-text-muted">Genommen</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{statistik.urlaubstageBeantragt}</div>
              <div className="text-sm text-text-muted">Beantragt</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{statistik.urlaubstageVerfuegbar}</div>
              <div className="text-sm text-text-muted">Verfügbar</div>
            </div>
          </div>
          {statistik.krankheitstage > 0 && (
            <div className="mt-4 pt-4 border-t border-border text-sm text-text-muted">
              Krankheitstage {jahr}: <span className="font-medium text-red-600">{statistik.krankheitstage}</span>
            </div>
          )}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Meine Anträge</h2>
        <select
          value={jahr}
          onChange={(e) => setJahr(parseInt(e.target.value))}
          className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {years.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Liste */}
      {abwesenheiten.length === 0 ? (
        <div className="bg-surface rounded-card shadow-card p-8 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-text-muted">Keine Abwesenheiten in {jahr}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {abwesenheiten.map(a => {
            const typConfig = TYP_CONFIG[a.typ] || TYP_CONFIG.SONSTIGES;
            const statusConfig = STATUS_CONFIG[a.status] || STATUS_CONFIG.BEANTRAGT;
            const StatusIcon = statusConfig.icon;
            const canEdit = a.status === 'BEANTRAGT';
            const canDelete = a.status === 'BEANTRAGT' || a.status === 'GENEHMIGT';

            return (
              <div key={a.id} className="bg-surface rounded-card shadow-card p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Typ & Status */}
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${typConfig.color}`}>
                        {typConfig.icon} {typConfig.label}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                    </div>

                    {/* Zeitraum */}
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-text-muted" />
                      <span className="font-medium">{formatDateRange(a.startDatum, a.endDatum)}</span>
                      {!a.ganztags && a.startzeit && a.endzeit && (
                        <span className="text-text-muted">({a.startzeit} - {a.endzeit})</span>
                      )}
                    </div>

                    {/* Grund */}
                    {a.grund && (
                      <p className="text-sm text-text-muted mt-2">{a.grund}</p>
                    )}

                    {/* Genehmiger / Ablehnungsgrund */}
                    {a.genehmiger && (
                      <p className="text-xs text-text-muted mt-2">
                        {a.status === 'GENEHMIGT' ? 'Genehmigt' : 'Bearbeitet'} von {a.genehmiger.name}
                        {a.bearbeitetAm && ` am ${formatDate(a.bearbeitetAm)}`}
                      </p>
                    )}
                    {a.ablehnungsgrund && (
                      <p className="text-sm text-red-600 mt-2">
                        Grund: {a.ablehnungsgrund}
                      </p>
                    )}
                  </div>

                  {/* Aktionen */}
                  <div className="flex items-center gap-1 shrink-0">
                    {canEdit && (
                      <button
                        onClick={() => onEdit(a)}
                        className="p-2 text-text-muted hover:text-primary hover:bg-primary-light rounded-lg transition"
                        title="Bearbeiten"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(a.id)}
                        disabled={deleting === a.id}
                        className="p-2 text-text-muted hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                        title={a.status === 'GENEHMIGT' ? 'Stornieren' : 'Löschen'}
                      >
                        <Trash2 className={`w-4 h-4 ${deleting === a.id ? 'animate-pulse' : ''}`} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AbwesenheitMeine;
